import React, { useState } from "react";
import { LayoutGrid, MoreHorizontal, Plus, Zap } from "lucide-react";

import { AppLogo } from "@/shared/components/AppLogo";
import { Counted, ListCard, ListEmpty, ListRow } from "@/shared/components/ListCard";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Chip, GhostButton } from "@/shared/components/ui-kit";
import { confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import {
  useAgentLinks, useConnections, useRemoveAppSource, useTriggers, useUnlinkConnection,
} from "@/shared/api/apps";
import { AppTriggersDialog } from "./AppTriggersDialog";
import { ConnectAppDialog } from "./ConnectAppDialog";
import { WhoseAccountDialog } from "./WhoseAccount";

import type { AgentAppLink, AppConnection, ConnectionAppRef } from "@/shared/types";

/* The agent's apps, grouped by whose account each uses (CONTRACT §7, tasks/end-customers 09):
   included on the owner's, each customer's own, or none. That is the business decision
   behind each app — the owner pays for what runs on theirs; a customer brings their own —
   so it is what the list is read by, and each row's menu switches it in place.

   An owner's row is one account *linked* to the agent, not a connect switch: it is
   authorized once, here or on the Apps page. What a row adds and removes is the link, and
   the only status worth a chip is a credential that has stopped working. The account's
   triggers are one click away in AppTriggersDialog. */

type Action = { label: string; run: () => void; danger?: boolean };

/** Every row's actions sit behind one menu: they are few, and two of them change who pays.
    The span keeps the menu's clicks off the row: React bubbles a portal's events up its
    component tree, so an item picked would open the row's triggers too. */
function RowMenu({ name, actions }: { name: string; actions: Action[] }) {
  return (
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Actions for ${name}`}
            className="press w-7 h-7 shrink-0 rounded-[8px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
          >
            <MoreHorizontal className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-[12px] bg-[var(--surface)] border-[var(--hairline-strong)] shadow-menu p-1">
          {actions.map((action) => (
            // Deferred past Radix's close/focus-restore so a dialog opens cleanly.
            <DropdownMenuItem key={action.label} className={`px-2 py-[5px] rounded-[8px] text-[13px] ${action.danger ? "text-[var(--danger)]" : ""}`} onSelect={() => setTimeout(action.run, 0)}>
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

function Group({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="px-4 pt-3 pb-2 bg-[var(--surface-sunk)]/40">
      <div className="section-label">{title}</div>
      <div className="mt-0.5 text-[12px] text-[var(--text-tertiary)]">{hint}</div>
    </div>
  );
}

function RemoveConfirm({ name, consequence, onRemove, onClose }: {
  name: string;
  consequence: string;
  onRemove: () => void;
  onClose: () => void;
}) {
  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Remove {name} from this agent?</AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">{consequence}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full text-[13px]">Cancel</AlertDialogCancel>
          <button className={confirmDangerCls} onClick={() => { onClose(); onRemove(); }}>
            Remove
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** One of the owner's accounts linked to the agent: everyone who talks to it acts through it. */
function OwnerRow({ agentId, connection, triggers, appTriggers, onConfigure }: {
  agentId: string;
  connection: AppConnection;
  triggers: number;
  /** The agent's triggers on every account of this app here: a switch takes them all. */
  appTriggers: number;
  onConfigure: () => void;
}) {
  const [open, setOpen] = useState<"remove" | "whose" | null>(null);
  const unlink = useUnlinkConnection();
  const off = connection.link?.enabled === false;
  const broken = connection.status !== "connected";
  const { name } = connection.app;

  return (
    <>
      <ListRow
        onClick={onConfigure}
        lead={<AppLogo logo={connection.app.logo} name={name} size={32} iconSize={18} rounded={8} className={off ? "opacity-40 grayscale" : ""} />}
        title={name}
        desc={connection.account_label}
        muted={off}
      >
        {broken ? (
          <Chip tone="danger">Reconnect</Chip>
        ) : off ? (
          <Chip>Off</Chip>
        ) : triggers > 0 ? (
          <span className="shrink-0 flex items-center gap-1 text-[11px] font-medium tabular-nums text-[var(--text-tertiary)]">
            <Zap className="w-[10px] h-[10px]" strokeWidth={2} />{triggers}
          </span>
        ) : null}
        <RowMenu
          name={name}
          actions={[
            { label: "Triggers", run: onConfigure },
            { label: "Use each customer's own account", run: () => setOpen("whose") },
            { label: "Remove from agent", run: () => setOpen("remove"), danger: true },
          ]}
        />
      </ListRow>
      {open === "whose" && (
        <WhoseAccountDialog agentId={agentId} app={connection.app} now="owner" triggers={appTriggers} onOpenChange={() => setOpen(null)} />
      )}
      {open === "remove" && (
        <RemoveConfirm
          name={name}
          consequence={`The agent loses its ${name} tools${triggers > 0 ? `, and ${triggers === 1 ? "its trigger is" : `all ${triggers} of its triggers are`} deleted` : ""}. The account stays connected for everything else.`}
          onRemove={() => unlink.mutate({ id: connection.id, agentId })}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/** An app with no account of the owner's on it: each customer's own, or none at all. */
function SourceRow({ agentId, link }: { agentId: string; link: AgentAppLink }) {
  const [open, setOpen] = useState<"remove" | "whose" | null>(null);
  const remove = useRemoveAppSource();
  const theirs = link.account === "customer";
  const connected = link.customers_connected ?? 0;
  const { name } = link.app;
  const app: ConnectionAppRef = link.app;

  return (
    <>
      <ListRow
        lead={<AppLogo logo={link.app.logo} name={name} size={32} iconSize={18} rounded={8} />}
        title={name}
        desc={theirs ? (connected ? <><Counted n={connected} noun="customer" /> connected</> : "No customer has connected one yet") : "Needs no account"}
      >
        <RowMenu
          name={name}
          actions={[
            ...(theirs ? [{ label: "Use my account", run: () => setOpen("whose") }] : []),
            { label: "Remove from agent", run: () => setOpen("remove"), danger: true },
          ]}
        />
      </ListRow>
      {open === "whose" && (
        <WhoseAccountDialog agentId={agentId} app={app} now="customer" customers={connected} onOpenChange={() => setOpen(null)} />
      )}
      {open === "remove" && (
        <RemoveConfirm
          name={name}
          consequence={`The agent loses its ${name} tools.${connected ? ` The ${connected === 1 ? "customer" : `${connected} customers`} who connected their own ${name} account ${connected === 1 ? "is" : "are"} disconnected from it.` : ""}`}
          onRemove={() => remove.mutate({ slug: link.app.slug, agentId })}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

export function ConnectedApps({ agentId }: { agentId: string }) {
  const { data: connections, isPending } = useConnections(agentId);
  const { data: subs } = useTriggers(agentId);
  const { data: links } = useAgentLinks(agentId);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const rows = connections ?? [];
  const theirs = (links ?? []).filter((l) => l.account === "customer");
  const none = (links ?? []).filter((l) => l.account === "none");
  const open = rows.find((c) => c.id === configuring);
  const triggersOn = (id: string) => (subs ?? []).filter((s) => s.connection_id === id).length;
  const triggersOnApp = (slug: string) => rows.filter((c) => c.app.slug === slug).reduce((n, c) => n + triggersOn(c.id), 0);

  return (
    <>
      <ListCard
        label={isPending ? "Loading apps…" : <Counted n={rows.length + theirs.length + none.length} noun="app" />}
        action={<GhostButton onClick={() => setPicking(true)} className="h-7 px-2.5"><Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Connect app</GhostButton>}
      >
        {!isPending && rows.length + theirs.length + none.length === 0 && (
          <ListEmpty icon={LayoutGrid} title="No apps connected" hint="Connect Gmail, Slack, GitHub and more, and the agent can read and act in them." />
        )}
        {rows.length > 0 && <Group title="Included" hint="Runs on your account for everyone who talks to the agent." />}
        {rows.map((c) => (
          <OwnerRow
            key={c.id}
            agentId={agentId}
            connection={c}
            triggers={triggersOn(c.id)}
            appTriggers={triggersOnApp(c.app.slug)}
            onConfigure={() => setConfiguring(c.id)}
          />
        ))}
        {theirs.length > 0 && <Group title="Each customer connects their own" hint="On the agent's page, the first time it's needed." />}
        {theirs.map((l) => <SourceRow key={l.app.slug} agentId={agentId} link={l} />)}
        {none.length > 0 && <Group title="No account needed" hint="Works for everyone as it is." />}
        {none.map((l) => <SourceRow key={l.app.slug} agentId={agentId} link={l} />)}
      </ListCard>

      {picking && (
        <ConnectAppDialog
          agentId={agentId}
          linked={rows.map((c) => c.id)}
          added={[...theirs, ...none].map((l) => l.app.slug)}
          onOpenChange={setPicking}
        />
      )}
      {open && (
        <AppTriggersDialog
          key={open.id}
          agentId={agentId}
          connection={open}
          onOpenChange={(o) => !o && setConfiguring(null)}
        />
      )}
    </>
  );
}
